<template>
  <div class="ae">

    <div class="ae-grid" :class="{ 'has-drawer': showHistory }">
      <!-- ─────────── Sessions drawer ─────────── -->
      <aside v-if="showHistory" class="ae-card" aria-label="Sessions">
        <div class="ae-card-head ae-card-head-stack">
          <div class="ae-title">Sessions</div>
          <div class="ae-head-right">
            <span v-if="pastSessions.length" class="ae-state">{{ pastSessions.length }}</span>
            <a href="#" class="ae-link" @click.prevent="showHistory = false">Close</a>
          </div>
        </div>
        <div class="ae-drawer-list">
          <p v-if="!pastSessions.length" class="ae-muted" style="padding: 12px 16px">No sessions yet.</p>
          <button
            v-for="ps in drawerRows"
            :key="ps.session_id"
            type="button"
            class="ae-history-row"
            :class="{ 'is-open': ps.isOpen }"
            title="Open this session"
            @click="resumeSession(ps.session_id)"
          >
            <span class="ae-history-title">{{ ps.title || '(no message)' }}</span>
            <span v-if="ps.description" class="ae-history-desc">{{ ps.description }}</span>
            <span class="ae-history-meta">{{ formatWhen(ps.created_at) }}</span>
          </button>
        </div>
      </aside>

      <!-- ─────────── Conversation ─────────── -->
      <section class="ae-card" aria-label="Agent session">
        <div class="ae-card-head ae-card-head-stack">
          <div class="ae-title">{{ conversationTitle }}</div>
          <div class="ae-head-right">
            <span class="ae-state">
              <span class="ae-dot" :style="{ background: stateDotColor }" />
              <span>{{ stateLabel }}</span>
              <span v-if="s.totalCost" class="ae-cost">${{ s.totalCost.toFixed(2) }}</span>
              <span v-if="contextUsage" class="ae-cost" :class="contextUsage.tone" :title="`${s.promptTokens.toLocaleString()} of ${s.contextLimit.toLocaleString()} context tokens`">{{ contextUsage.text }}</span>
            </span>
            <a href="#" class="ae-link" @click.prevent="toggleHistory">Sessions</a>
            <a href="#" class="ae-link" @click.prevent="startLive">{{ sessionActive || s.connState !== 'idle' ? 'New' : 'Start' }}</a>
            <a v-if="sessionActive" href="#" class="ae-link" :class="{ 'is-urgent': contextUsage && contextUsage.tone === 'is-error' }" title="Start a new session that carries this one's artifacts; the agent reads this conversation first" @click.prevent="continueSession">Continue</a>
            <a v-if="sessionActive" href="#" class="ae-link" @click.prevent="endSession">
              {{ s.connState === 'launching' || s.connState === 'connecting' ? 'Cancel' : 'End' }}
            </a>
          </div>
        </div>
        <div ref="scroll" class="ae-log">
          <div v-if="!s.blocks.length" class="ae-empty">
            <div class="ae-empty-title">{{ emptyTitle }}</div>
            <div class="ae-empty-body">{{ emptyBody }}</div>
          </div>

          <template v-for="(b, i) in s.blocks" :key="i">
            <div v-if="b.kind === 'user'" class="ae-row">
              <div class="ae-avatar ae-avatar-human">{{ userInitials }}</div>
              <div class="ae-col">
                <div class="ae-who">You</div>
                <div class="ae-bubble sensitive" data-demo-blur>{{ b.text }}</div>
              </div>
            </div>

            <div v-else-if="b.kind === 'text'" class="ae-row">
              <div class="ae-avatar ae-avatar-agent">K</div>
              <div class="ae-col">
                <div class="ae-who">{{ agentLabel }}</div>
                <div class="ae-bubble ae-prose sensitive" data-demo-blur v-html="renderMarkdown(b.text)" />
              </div>
            </div>

            <div v-else-if="b.kind === 'report'" class="ae-row">
              <div class="ae-avatar ae-avatar-agent">K</div>
              <div class="ae-col">
                <div class="ae-who">{{ agentLabel }}</div>
                <div class="ae-report">
                  <div class="ae-report-title">{{ b.title }}</div>
                  <div v-for="(it, j) in b.items" :key="j" class="ae-item">
                    <span class="ae-check">✓</span>
                    <span class="sensitive" data-demo-blur v-html="renderInline(it)" />
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="b.kind === 'approval'" class="ae-row">
              <div class="ae-avatar ae-avatar-agent">K</div>
              <div class="ae-col ae-clarify">
                <div class="ae-clarify-q">Review this change · {{ b.operationKind }}</div>
                <pre class="ae-code sensitive" data-demo-blur>{{ JSON.stringify(b.payload, null, 2) }}</pre>
                <div v-if="!b.answered" class="ae-answer">
                  <button type="button" class="ae-btn" :disabled="b.posting" @click="approveOperation(b, true)">Approve change</button>
                  <button type="button" class="ae-btn" :disabled="b.posting" @click="approveOperation(b, false)">Decline</button>
                </div>
                <div v-else class="ae-answered">{{ b.answered }}</div>
                <div v-if="b.error" role="alert" class="ae-small sensitive" data-demo-blur>{{ b.error }}</div>
              </div>
            </div>

            <div v-else-if="b.kind === 'question'" class="ae-row">
              <div class="ae-avatar ae-avatar-agent">K</div>
              <div class="ae-col">
                <div class="ae-who">{{ agentLabel }}{{ b.answered ? '' : ' · waiting for your answer' }}</div>
                <div class="ae-clarify">
                  <div class="ae-clarify-q sensitive" data-demo-blur v-html="renderMarkdown(b.question)" />
                  <button
                    v-for="(option, j) in b.options"
                    :key="option.label"
                    type="button"
                    class="ae-option"
                    :class="{ 'is-chosen': b.answered === option.label, 'is-dim': b.answered && b.answered !== option.label }"
                    :disabled="!!b.answered"
                    @click="answerQuestion(b, option.label)"
                  >
                    <span class="ae-num">{{ j + 1 }}</span>
                    <span><strong>{{ option.label }}</strong><span v-if="option.description"> — {{ option.description }}</span></span>
                  </button>
                  <div v-if="!b.answered" class="ae-answer">
                    <input
                      v-model="b.responseText"
                      class="ae-answer-input"
                      placeholder="Or write your answer"
                      @keydown.enter.prevent="answerQuestion(b)"
                    />
                    <button type="button" class="ae-btn" :disabled="!b.responseText || !b.responseText.trim()" @click="answerQuestion(b)">Answer</button>
                  </div>
                  <div v-else class="ae-answered">
                    {{ b.answered === '(expired)' ? 'Left unanswered in an earlier session.' : `Answer: ${b.answered}` }}
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="b.kind === 'thinking'" class="ae-aside">
              <button v-if="b.done && b.text.trim()" type="button" class="ae-link-muted" @click="b.collapsed = !b.collapsed">
                {{ b.collapsed ? 'Show reasoning' : 'Hide reasoning' }}
              </button>
              <div v-if="b.done && !b.collapsed" class="ae-reasoning sensitive" data-demo-blur>{{ b.text }}</div>
            </div>

            <div v-else-if="b.kind === 'created'" class="ae-aside">
              <button type="button" class="ae-created" :class="{ 'is-error': !b.ok }" @click="b.ok && scrollToArtifact(b.artifactKey)">
                <span class="ae-check" :style="b.ok ? '' : 'color:#D53D3D'">{{ b.ok ? '✓' : '✕' }}</span>
                <span v-if="b.ok"><strong>{{ b.artifactLabel }} {{ b.action }}</strong> · {{ b.name }}</span>
                <span v-else>{{ b.artifactLabel }} not saved · {{ b.error }}</span>
              </button>
            </div>

            <div v-else-if="b.kind === 'upload'" class="ae-upload" :class="b.ok ? 'is-ok' : 'is-error'">
              <div class="ae-upload-title">{{ b.ok ? 'Uploaded' : 'Upload failed' }} · {{ b.name }}</div>
              <div v-if="b.ok" class="ae-upload-meta ae-mono">{{ b.key }}</div>
              <div v-else class="ae-upload-meta">{{ b.error }}</div>
            </div>

            <div v-else-if="b.kind === 'action'" class="ae-aside ae-small ae-run-line">{{ actionVerb(b.verb) }} <span class="ae-mono sensitive">{{ b.targetId }}</span></div>
            <div v-else-if="b.kind === 'stopped'" class="ae-aside ae-small">Stopped.</div>
            <div v-else-if="b.kind === 'error'" class="ae-aside ae-small" style="color:#D53D3D">{{ b.text }}</div>
            <div v-else-if="b.kind === 'meta'" class="ae-aside ae-small">{{ b.text }}</div>

            <div v-else-if="b.kind === 'session_end'" class="ae-end">
              <div class="ae-end-title">Session ended. {{ persistedArtifacts.length ? 'Capability remains.' : 'Nothing was saved.' }}</div>
              <div class="ae-end-text">{{ artifactSummary }}</div>
            </div>
          </template>

          <div v-if="thinking && !liveThinkingBlock" class="ae-typing">{{ workingText }}</div>
        </div>

        <div class="ae-composer">
          <div class="ae-composer-box" :class="{ 'is-disabled': composerDisabled }">
            <textarea
              v-model="draft"
              rows="1"
              class="ae-textarea"
              :placeholder="composerPlaceholder"
              :disabled="composerDisabled"
              @keydown.enter.exact.prevent="sendDraft"
            />
            <button v-if="thinking" type="button" class="ae-btn" @click="interrupt">Stop</button>
            <button type="button" class="ae-btn ae-btn-primary" :disabled="!draft.trim() || composerDisabled" @click="sendDraft">Send</button>
          </div>
        </div>
      </section>

      <!-- ─────────── Notebook ─────────── -->
      <section class="ae-card" aria-label="Notebook">
        <div class="ae-card-head ae-card-head-stack">
          <div class="ae-title">Notebook</div>
          <div class="ae-head-right">
            <span class="ae-state">{{ notebookState }}</span>
          </div>
        </div>

        <div ref="artifactScroll" class="ae-artifacts">
          <div v-if="!notebookItems.length" class="ae-empty">
            <div class="ae-empty-body">Every query the agent runs lands here as a block with its result beneath. Edit a block, then ask the agent to run it.</div>
          </div>

          <template v-for="a in notebookItems" :key="a.key">
            <!-- draft -->
            <div v-if="a.kind === 'draft'" :id="artifactDomId(a.key)" class="ae-cell ae-draft" :class="{ 'is-editing': a.editing, 'is-flash': a.flash }">
              <div class="ae-cell-meta">
                <font-awesome-icon :icon="kindIcon(a.draftKind)" class="ae-kind-icon" :style="{ color: kindColor(a.draftKind) }" />
                <span class="ae-cell-engine">{{ a.label }}</span>
                <span class="ae-cell-id sensitive">{{ a.name }}</span>
                <span class="ae-draft-chip" :class="`is-${a.status}`">{{ draftChip(a) }}</span>
                <span class="ae-cell-actions">
                  <template v-if="a.editing">
                    <a href="#" class="ae-link" @click.prevent="saveDraftEdit(a)">Save edit</a>
                    <a href="#" class="ae-link" @click.prevent="cancelDraftEdit(a)">Cancel</a>
                  </template>
                  <template v-else>
                    <a v-if="sessionActive" href="#" class="ae-link" @click.prevent="startDraftEdit(a)">Edit</a>
                    <a v-if="sessionActive && a.kind !== 'schema'" href="#" class="ae-link" @click.prevent="draftAction('preview', a)">Preview</a>
                    <a v-if="sessionActive && a.status !== 'saved'" href="#" class="ae-link" :title="a.savedId ? 'Save the changes to the platform' : 'Save to the platform (the agent asks you to approve)'" @click.prevent="draftAction('save', a)">Save</a>
                    <a v-if="sessionActive" href="#" class="ae-link" @click.prevent="draftAction('discard', a)">Discard</a>
                  </template>
                </span>
              </div>
              <div v-if="codeField(a)" class="ae-cell-box ae-cell-box-code">
                <div class="ae-cell-strip">
                  <span>{{ codeField(a) === 'script' ? 'R script' : 'SQL' }}</span>
                  <span v-if="a.editing" class="is-muted">editing</span>
                  <span v-else-if="a.by === 'user'" class="is-stale">edited by you · v{{ a.version }}</span>
                  <span v-else class="is-muted">written by the agent · v{{ a.version }}</span>
                </div>
                <div v-if="a.error" class="ae-cell-error sensitive" data-demo-blur>{{ a.error }}</div>
                <div v-if="a.savedAt" class="ae-draft-saved">Saved as #{{ a.savedId }} · {{ formatWhen(a.savedAt) }}</div>
                <textarea
                  v-if="a.editing"
                  v-model="a.edit.code"
                  class="ae-cell-editor sensitive"
                  spellcheck="false"
                  :rows="editorRows(a.edit.code)"
                  @keydown.meta.enter.prevent="saveDraftEdit(a)"
                  @keydown.ctrl.enter.prevent="saveDraftEdit(a)"
                  @keydown.esc.prevent="cancelDraftEdit(a)"
                />
                <pre v-else class="ae-cell-pre sensitive">{{ draftCode(a) }}</pre>
              </div>
              <div class="ae-cell-box ae-cell-box-code" :class="{ 'ae-cell-box-json': codeField(a) }">
                <div class="ae-cell-strip ae-cell-strip-toggle" @click="toggleDraft(a)">
                  <span><span class="ae-draft-caret">{{ a.collapsed && !a.editing ? '▸' : '▾' }}</span>{{ codeField(a) ? 'Everything else · JSON' : `${a.label} · JSON spec` }}</span>
                  <template v-if="!codeField(a)">
                    <span v-if="a.editing" class="is-muted">editing</span>
                    <span v-else-if="a.by === 'user'" class="is-stale">edited by you · v{{ a.version }}</span>
                    <span v-else class="is-muted">written by the agent · v{{ a.version }}</span>
                  </template>
                </div>
                <template v-if="!codeField(a)">
                  <div v-if="a.error" class="ae-cell-error sensitive" data-demo-blur>{{ a.error }}</div>
                  <div v-if="a.savedAt" class="ae-draft-saved">Saved as #{{ a.savedId }} · {{ formatWhen(a.savedAt) }}</div>
                </template>
                <template v-if="a.editing">
                  <textarea
                    v-model="a.edit.json"
                    class="ae-cell-editor ae-draft-editor ae-mono sensitive"
                    spellcheck="false"
                    :rows="editorRows(a.edit.json)"
                    @keydown.meta.enter.prevent="saveDraftEdit(a)"
                    @keydown.ctrl.enter.prevent="saveDraftEdit(a)"
                    @keydown.esc.prevent="cancelDraftEdit(a)"
                  />
                  <div v-if="a.editError" class="ae-cell-error sensitive" data-demo-blur>{{ a.editError }}</div>
                </template>
                <pre v-else-if="!a.collapsed" class="ae-cell-pre ae-cell-pre-json sensitive">{{ draftJson(a) }}</pre>
              </div>
              <div v-if="a.note && !draftPreview(a)" class="ae-cell-box ae-cell-box-result">
                <div class="ae-cell-strip ae-cell-strip-result">
                  <span>Check · <span class="is-ok">valid</span></span>
                  <span v-if="a.checkedAt" class="is-muted">last check · {{ formatWhen(a.checkedAt) }}</span>
                </div>
                <div class="ae-cell-note ae-draft-check sensitive" data-demo-blur>{{ a.note }}</div>
              </div>
              <template v-if="draftPreview(a)">
                <div class="ae-cell-box ae-cell-box-result">
                  <div class="ae-cell-strip ae-cell-strip-result">
                    <span>Preview<template v-if="draftPreview(a).result"> · <span :class="cellStatus(draftPreview(a)).tone">{{ cellStatus(draftPreview(a)).text }}</span></template></span>
                    <span v-if="draftPreview(a).result && draftPreview(a).result.ranAt" :class="previewStale(a) ? 'is-stale' : 'is-muted'">{{ previewStale(a) ? 'edited since preview' : 'last preview' }} · {{ formatWhen(draftPreview(a).result.ranAt) }}</span>
                  </div>
                  <details v-if="previewDiffers(a)" class="ae-draft-compiled">
                    <summary>Compiled SQL</summary>
                    <pre class="ae-cell-pre sensitive">{{ draftPreview(a).code }}</pre>
                  </details>
                  <div v-if="draftPreview(a).result && draftPreview(a).result.kind === 'sql'" class="ae-cell-result">
                    <div v-if="!draftPreview(a).result.ok" class="ae-cell-error sensitive" data-demo-blur>{{ draftPreview(a).result.error }}</div>
                    <div v-else-if="!draftPreview(a).result.rows.length" class="ae-cell-note">No rows.</div>
                    <div v-else class="ae-cell-scroll sensitive">
                      <table class="ae-table ae-cell-table">
                        <thead><tr><th v-for="c in draftPreview(a).result.columns" :key="c">{{ c }}</th></tr></thead>
                        <tbody>
                          <tr v-for="(r, ri) in draftPreview(a).result.rows" :key="ri"><td v-for="c in draftPreview(a).result.columns" :key="c" class="ae-mono">{{ r[c] }}</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-if="draftPreview(a).result.ok && draftPreview(a).result.truncated" class="ae-cell-note">{{ draftPreview(a).result.rows.length }} of {{ formatCount(draftPreview(a).result.rowCount) }} rows</div>
                  </div>
                </div>
              </template>
            </div>

            <!-- code block -->
            <div v-else-if="a.kind === 'cell'" :id="artifactDomId(a.key)" class="ae-cell" :class="{ 'is-editing': a.editing, 'is-flash': a.flash }">
              <div class="ae-cell-meta">
                <font-awesome-icon :icon="kindIcon(a.engine)" class="ae-kind-icon" :style="{ color: kindColor(a.engine) }" />
                <span class="ae-cell-engine">{{ engineLabel(a.engine) }}</span>
                <span class="ae-cell-id sensitive">{{ a.label || a.id }}</span>
                <span class="ae-cell-actions">
                  <template v-if="a.editing">
                    <a href="#" class="ae-link" @click.prevent="saveEdit(a)">Save</a>
                    <a href="#" class="ae-link" @click.prevent="cancelEdit(a)">Cancel</a>
                  </template>
                  <template v-else>
                    <a v-if="sessionActive" href="#" class="ae-link" @click.prevent="startEdit(a)">Edit</a>
                    <a v-if="sessionActive" href="#" class="ae-link" @click.prevent="runCell(a)">Run</a>
                  </template>
                </span>
              </div>
              <div class="ae-cell-box ae-cell-box-code">
                <div class="ae-cell-strip">
                  <span>{{ a.engine === 'r' ? 'R script' : 'SQL' }}</span>
                  <span v-if="a.editing" class="is-muted">editing</span>
                  <span v-else-if="a.by === 'user'" class="is-stale">edited by you</span>
                  <span v-else class="is-muted">written by the agent</span>
                </div>
                <div class="ae-cell-code">
                  <textarea
                    v-if="a.editing"
                    v-model="a.draft"
                    class="ae-cell-editor sensitive"
                    spellcheck="false"
                    :rows="editorRows(a.draft)"
                    @keydown.meta.enter.prevent="saveEdit(a)"
                    @keydown.ctrl.enter.prevent="saveEdit(a)"
                    @keydown.esc.prevent="cancelEdit(a)"
                  />
                  <pre v-else class="ae-cell-pre sensitive">{{ a.code }}</pre>
                </div>
              </div>
              <div class="ae-cell-box ae-cell-box-result">
                <div class="ae-cell-strip ae-cell-strip-result">
                  <span>Result<template v-if="a.result"> · <span :class="cellStatus(a).tone">{{ cellStatus(a).text }}</span></template></span>
                  <span v-if="a.result && a.result.ranAt" :class="cellStale(a) ? 'is-stale' : 'is-muted'">{{ cellStale(a) ? 'edited since last run' : 'last run' }} · {{ formatWhen(a.result.ranAt) }}</span>
                  <span v-else-if="!a.result" class="is-muted">not run yet</span>
                </div>
                <div v-if="a.result && a.result.kind === 'sql'" class="ae-cell-result">
                  <div v-if="!a.result.ok" class="ae-cell-error sensitive" data-demo-blur>{{ a.result.error }}</div>
                  <div v-else-if="!a.result.rows.length" class="ae-cell-note">No rows.</div>
                  <div v-else class="ae-cell-scroll sensitive">
                    <table class="ae-table ae-cell-table">
                      <thead><tr><th v-for="c in a.result.columns" :key="c">{{ c }}</th></tr></thead>
                      <tbody>
                        <tr v-for="(r, ri) in a.result.rows" :key="ri"><td v-for="c in a.result.columns" :key="c" class="ae-mono">{{ r[c] }}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="a.result.ok && a.result.truncated" class="ae-cell-note">{{ a.result.rows.length }} of {{ formatCount(a.result.rowCount) }} rows</div>
                </div>
                <div v-else-if="a.result && a.result.kind === 'r'" class="ae-cell-result ae-cell-result-r">
                  <AgentRunOutput :status="a.result.status" :error="a.result.error" :console-text="a.result.console" :outputs="a.result.outputs" :plot-urls="a.result.plotUrls" />
                </div>
              </div>
              <div v-if="a.error" class="ae-cell-error sensitive" data-demo-blur>{{ a.error }}</div>
            </div>

            <div v-else :id="artifactDomId(a.key)" class="ae-artifact" :class="{ 'is-flash': a.flash }">
              <div class="ae-artifact-head">
                <div class="ae-artifact-label">
                  <font-awesome-icon :icon="kindIcon(a.kind)" class="ae-kind-icon" :style="{ color: kindColor(a.kind) }" />
                  <span class="ae-dot" :style="{ background: statusDot(a.status) }" />
                  {{ a.label }}<span v-if="a.name" class="ae-artifact-name sensitive"> · {{ a.name }}</span>
                </div>
                <div class="ae-artifact-meta">{{ a.status }}</div>
              </div>

              <!-- visual -->
              <div v-if="a.kind === 'visual'" class="ae-box sensitive">
                <div v-if="!a.viz" style="color:#D53D3D">Could not render this visual.</div>
                <Chart_V6 v-else-if="a.viz.component === 'chart_v6'" :spec="a.viz.props.spec" />
                <div v-else-if="a.viz.component === 'chart_v5'" style="height:300px">
                  <Chart_V5 :chart_data="a.viz.props.chart_data" :chart_config="a.viz.props.chart_config" override-height="h-full" />
                </div>
                <div v-else-if="a.viz.component === 'table'" class="ae-table-scroll">
                  <table class="ae-table">
                    <thead><tr><th v-for="c in a.viz.props.columnDefs" :key="c.field">{{ c.headerName || c.field }}</th></tr></thead>
                    <tbody>
                      <tr v-for="(r, ri) in a.viz.props.rowData" :key="ri">
                        <td v-for="c in a.viz.props.columnDefs" :key="c.field">{{ r[c.field] }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <Pie_Chart v-else-if="a.viz.component === 'pie'" style="height:300px" override-height="h-full" :chart_data="a.viz.props.chart_data" :chart_config="a.viz.props.chart_config" />
                <Sankey_Diagram v-else-if="a.viz.component === 'sankey'" :chart-data="a.viz.props.chartData" :config="a.viz.props.config" />
                <HexMap v-else-if="a.viz.component === 'heatmap'" :chart-data="a.viz.props.chartData" :chart-config="a.viz.props.chartConfig" />
              </div>

              <!-- drawing -->
              <div v-else-if="a.kind === 'drawing'" class="ae-box sensitive">
                <iframe :srcdoc="drawingDoc(a)" sandbox="" class="ae-iframe" :style="{ aspectRatio: drawingAspect(a) }" :title="a.name" />
              </div>

              <!-- plan -->
              <div v-else-if="a.kind === 'plan'" class="ae-table-wrap">
                <table class="ae-table">
                  <thead><tr><th>Source</th><th>Role</th><th>Status</th><th>Description</th></tr></thead>
                  <tbody>
                    <tr v-for="src in a.sources" :key="src.name">
                      <td class="ae-mono">{{ src.name }}</td>
                      <td class="ae-muted">{{ src.role || '' }}</td>
                      <td><span class="ae-dot" :style="{ background: statusDot(src.status || 'planned') }" /> {{ src.status || 'planned' }}</td>
                      <td class="ae-muted">{{ src.description || '' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- proposal -->
              <template v-else-if="a.kind === 'proposal'">
                <div class="ae-facts">
                  <div class="ae-fact"><div class="ae-fact-k">Target</div><div class="ae-fact-v ae-mono">{{ a.name }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Key</div><div class="ae-fact-v">{{ a.append_only ? 'Append-only' : ((a.primary_key_columns || []).join(', ') || 'No key') }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Columns</div><div class="ae-fact-v">{{ (a.columns || []).length }}</div></div>
                </div>
                <div v-if="a.append_only && a.append_only_reason" class="ae-small ae-muted">{{ a.append_only_reason }}</div>
                <div v-if="a.error" class="ae-small" style="color:#D53D3D">{{ a.error }}</div>
                <div class="ae-table-wrap ae-table-scroll">
                  <table class="ae-table">
                    <thead><tr><th>Column</th><th>Type</th></tr></thead>
                    <tbody>
                      <tr v-for="col in a.columns" :key="col.name || col">
                        <td class="ae-mono">{{ col.name || col }}</td>
                        <td class="ae-muted">{{ col.snowflake_type || 'VARCHAR' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <!-- file -->
              <template v-else-if="a.kind === 'file'">
                <div class="ae-facts">
                  <div class="ae-fact" style="grid-column: span 2"><div class="ae-fact-k">File</div><div class="ae-fact-v ae-mono ae-break">{{ a.key }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Shape</div><div class="ae-fact-v">{{ a.note }}</div></div>
                </div>
                <div v-if="a.rows && a.rows.length" class="ae-table-wrap ae-cell-scroll sensitive">
                  <table class="ae-table ae-cell-table">
                    <thead><tr><th class="ae-file-rownum">row</th><th v-for="(c, ci) in a.rows[0]" :key="ci">{{ ci + 1 }}</th></tr></thead>
                    <tbody>
                      <tr v-for="(r, ri) in a.rows" :key="ri">
                        <td class="ae-mono ae-file-rownum">{{ (a.offset || 0) + ri }}</td>
                        <td v-for="(c, ci) in r" :key="ci" class="ae-mono">{{ c }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <!-- run without a block (sessions before the notebook) -->
              <AgentRunOutput v-else-if="a.kind === 'run'" :status="a.status" :error="a.error" :console-text="a.console" :outputs="a.outputs" :plot-urls="a.plotUrls" />
            </div>
          </template>
        </div>
      </section>

      <!-- ─────────── Files ─────────── -->
      <aside class="ae-card" aria-label="Artifacts">
        <div class="ae-card-head ae-card-head-stack">
          <div class="ae-title">Artifacts</div>
          <div class="ae-head-right">
            <span class="ae-state">
              <span class="ae-dot" :style="{ background: sessionPill.dot }" />
              <span>{{ sessionPill.text }}</span>
            </span>
            <a href="#" class="ae-link" @click.prevent="showTables = true">Tables</a>
            <a href="#" class="ae-link" @click.prevent="showUpload = true">Add data</a>
          </div>
        </div>
        <div class="ae-files">
          <div v-if="!filesItems.length && !s.files.length && !s.drafts.length && !outputItems.length" class="ae-empty ae-files-empty">
            <div class="ae-empty-body">Everything in the notebook, indexed: drafts, outputs, files you add, and what gets saved to the platform.</div>
          </div>

          <div v-if="s.drafts.length" class="ae-files-sect">Drafts</div>
          <div v-for="d in s.drafts" :key="d.key" class="ae-file">
            <button type="button" class="ae-file-row" :title="`Go to ${d.name} in the notebook`" @click="scrollToArtifact(d.key)">
              <span class="ae-dot" :style="{ background: draftDot(d) }" />
              <font-awesome-icon :icon="kindIcon(d.kind)" class="ae-kind-icon" :style="{ color: kindColor(d.kind) }" />
              <span class="ae-file-kind">{{ d.label }}</span>
              <span class="ae-file-name sensitive">{{ d.name }}</span>
              <span class="ae-draft-chip" :class="`is-${d.status}`">{{ draftChip(d) }}</span>
            </button>
          </div>

          <div v-if="outputItems.length" class="ae-files-sect">Outputs</div>
          <div v-for="o in outputItems" :key="`out:${o.key}`" class="ae-file">
            <button type="button" class="ae-file-row" :title="`Go to ${o.name} in the notebook`" @click="scrollToArtifact(o.key)">
              <span class="ae-dot" :style="{ background: o.dot }" />
              <font-awesome-icon :icon="kindIcon(o.iconKind)" class="ae-kind-icon" :style="{ color: kindColor(o.iconKind) }" />
              <span class="ae-file-kind">{{ o.label }}</span>
              <span class="ae-file-name sensitive" :class="{ 'ae-mono': o.mono }">{{ o.name }}</span>
            </button>
          </div>

          <div v-if="filesItems.length" class="ae-files-sect">Saved</div>
          <div v-if="s.files.length" class="ae-files-sect">Uploads</div>
          <div v-for="f in s.files" :key="`upload:${f.key}`" class="ae-file">
            <button type="button" class="ae-file-row" :title="f.key" @click="f.expanded = !f.expanded">
              <span class="ae-dot" style="background: #385883" />
              <font-awesome-icon :icon="kindIcon('upload')" class="ae-kind-icon" :style="{ color: kindColor('upload') }" />
              <span class="ae-file-kind">Upload</span>
              <span class="ae-file-name sensitive">{{ f.name }}</span>
            </button>
            <div v-if="f.expanded" class="ae-file-body">
              <div class="ae-file-fact"><span>S3 key</span><span class="ae-mono ae-break">{{ f.key }}</span></div>
            </div>
          </div>
          <div v-for="a in filesItems" :id="artifactDomId(a.key)" :key="a.key" class="ae-file">
            <button type="button" class="ae-file-row" @click="a.expanded = !a.expanded">
              <span class="ae-dot" :style="{ background: statusDot(a.status) }" />
              <font-awesome-icon :icon="kindIcon(a.kind)" class="ae-kind-icon" :style="{ color: kindColor(a.kind) }" />
              <span class="ae-file-kind">{{ a.label }}</span>
              <span class="ae-file-name sensitive" :class="{ 'ae-mono': a.mono }">{{ a.name }}</span>
            </button>
            <div v-if="a.expanded" class="ae-file-body">
              <div class="ae-file-fact"><span>Status</span><span>{{ a.status }}</span></div>
              <div v-for="f in factsFor(a).slice(0, 5)" :key="f.k" class="ae-file-fact"><span>{{ f.k }}</span><span class="sensitive">{{ f.v }}</span></div>
              <pre v-if="a.script" class="ae-cell-pre ae-file-script sensitive">{{ a.script }}</pre>
              <div class="ae-file-fact ae-file-runs-head"><span>Runs</span><span>{{ (a.runs || []).length }}</span></div>
              <div v-for="(r, j) in a.runs" :key="j" class="ae-file-run">
                <span class="ae-check" :style="['failed','error'].includes(String(r.status).toLowerCase()) ? 'color:#D53D3D' : ''">{{ ['failed','error'].includes(String(r.status).toLowerCase()) ? '✕' : '✓' }}</span>
                <span class="ae-mono ae-file-run-when">{{ formatWhen(r.when) }}</span>
                <span class="sensitive ae-file-run-text">{{ r.error || r.result }}</span>
              </div>
              <div v-if="!a.runs || !a.runs.length" class="ae-cell-note">{{ a.status === 'deleted' ? 'This artifact no longer exists.' : 'No runs yet.' }}</div>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <AgentTablesModal v-if="showTables" @close="showTables = false" />
    <AgentUploadModal v-if="showUpload" @close="showUpload = false" @uploaded="onUploaded" @failed="onUploadFailed" />
  </div>
</template>

<script>
import axios from 'axios';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import Chart_V6 from '@/components/widgetcomponents/Viz_Elements/Chart_V6.vue';
import Sankey_Diagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue';
import HexMap from '@/components/widgetcomponents/Viz_Elements/HexMap.vue';
import Pie_Chart from '@/components/widgetcomponents/Viz_Elements/Pie_Chart.vue';
import { drawingDoc } from '@/utils/agentSandbox';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import AgentTablesModal from './AgentTablesModal.vue';
import AgentUploadModal from './AgentUploadModal.vue';
import AgentRunOutput, { plotKey } from './AgentRunOutput.vue';
import {
  faSnowflake, faDatabase, faFlaskVial, faTableColumns, faGaugeHigh, faLayerGroup, faTableList,
  faChartSimple, faPenRuler, faListCheck, faFileLines, faPlay, faFileArrowUp, faFlag, faClipboardList,
  faLightbulb, faGears, faTable,
} from '@fortawesome/free-solid-svg-icons';

const KIND_ICONS = {
  snowflake: faSnowflake, postgres: faDatabase, r: faFlaskVial,
  data_attribute: faTableColumns, metric: faGaugeHigh, dataset: faLayerGroup, study: faFlaskVial, schema: faTableList,
  visual: faChartSimple, drawing: faPenRuler, plan: faListCheck, file: faFileLines, proposal: faTableList, run: faPlay,
  inputs: faTable, upload: faFileArrowUp, flag: faFlag, worklist: faClipboardList, insight: faLightbulb, routine: faGears,
};
const KIND_COLORS = {
  snowflake: '#29B5E8', postgres: '#336791', r: '#2167BA',
  data_attribute: '#385883', metric: '#F76734', dataset: '#389A6C', study: '#7C4DBD', schema: '#B8860B',
  visual: '#F76734', drawing: '#7C4DBD', plan: '#626264', file: '#B8860B', proposal: '#B8860B', run: '#2167BA',
  inputs: '#389A6C', upload: '#385883', flag: '#D53D3D', worklist: '#389A6C', insight: '#F1BD6C', routine: '#626264',
};

marked.setOptions({ breaks: true, gfm: true });

function renderMarkdown(text) {
  return DOMPurify.sanitize(marked.parse(String(text || '')));
}

function renderInline(text) {
  return DOMPurify.sanitize(marked.parseInline(String(text || '')));
}

import { sessionEntry, hasPendingQuestion, wakePolling, stopPolling, handleEvent, fetchArtifacts, pushBlock, turnUrl, applyEnvelope, applyRunOutput, artifactKey, upsertArtifact, runHolder, attachFile, applyDraftEdit, DRAFT_LEDGER_KIND, NOTEBOOK_ARTIFACT_KINDS } from '@/services/agentSessionController';

const ACTION_VERBS = { run: 'Running', preview: 'Previewing', save: 'Saving', discard: 'Discarding' };
const ACTION_TEXT = {
  run: (id) => `Run notebook block \`${id}\` as it is now.`,
  preview: (id) => `Preview draft \`${id}\`.`,
  save: (id) => `Save draft \`${id}\` to the platform.`,
  discard: (id) => `Discard draft \`${id}\`.`,
};

const ENGINE_LABELS = { snowflake: 'Snowflake', postgres: 'Postgres', r: 'R' };

export default {
  name: 'AgentExplore',
  components: {
    Chart_V5, Chart_V6, Sankey_Diagram, HexMap, Pie_Chart,
    AgentTablesModal, AgentUploadModal, AgentRunOutput,
  },
  props: {
    autoStart: { type: Boolean, default: false },
    metricId: { type: [Number, String], default: null },
    studyId: { type: [Number, String], default: null },
    s3Key: { type: String, default: '' },
    schemaId: { type: [Number, String], default: null },
  },
  data() {
    const entry = sessionEntry(
      `${this.$store.state.tenant.current_tenant_slug || ''}:${this.$store.state.auth.current_user?.id || ''}:${this.metricId || ''}:${this.studyId || ''}:${this.s3Key || ''}`,
    );
    return {
      entry,
      s: entry.state,
      draft: '',
      now: Date.now(),
      ticker: null,
      showHistory: false,
      pastSessions: [],
      resumeId: null,
      continueFrom: '',
      showTables: false,
      showUpload: false,
      runPollTimers: {},
    };
  },
  computed: {
    storageKey() {
      return `agent-session:${this.$store.state.tenant.current_tenant_slug || ''}:${this.$store.state.auth.current_user?.id || ''}:${this.metricId || ''}:${this.studyId || ''}:${this.s3Key || ''}`;
    },
    sessionActive() {
      return ['connecting', 'launching', 'ready', 'thinking', 'rate_limited'].includes(this.s.connState);
    },
    thinking() {
      return this.s.connState === 'thinking';
    },
    liveThinkingBlock() {
      return this.s.blocks.some((b) => b.kind === 'thinking' && !b.done);
    },
    hasPendingQuestion() {
      return hasPendingQuestion(this.entry);
    },
    composerDisabled() {
      return this.hasPendingQuestion || !this.sessionActive;
    },
    contextUsage() {
      const used = this.s.promptTokens;
      const limit = this.s.contextLimit;
      if (typeof used !== 'number' || !limit) return null;
      const pct = used / limit;
      const fmt = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(n));
      const tone = pct >= 0.9 ? 'is-error' : pct >= 0.7 ? 'is-stale' : '';
      return { text: `${fmt(used)} / ${fmt(limit)}${pct >= 0.9 ? ' · continue in a new session' : ''}`, tone };
    },
    thinkingElapsed() {
      if (!this.s.thinkingSince) return '';
      const secs = Math.max(0, Math.floor((this.now - this.s.thinkingSince) / 1000));
      return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
    },
    workingText() {
      return this.thinkingElapsed && this.thinkingElapsed !== '0s' ? `Working · ${this.thinkingElapsed}` : 'Working…';
    },
    stateLabel() {
      const st = this.s.connState;
      if (st === 'idle') return 'No session';
      if (st === 'connecting') return 'Connecting…';
      if (st === 'launching') return 'Starting the agent. You can send while it loads.';
      if (st === 'ready') return 'Ready';
      if (st === 'thinking') return `Working · ${this.thinkingElapsed}`;
      if (st === 'rate_limited') return `Rate limited · retrying in ${this.s.retryInSeconds || '?'}s`;
      return this.s.closeReason || 'Disconnected';
    },
    stateDotColor() {
      const st = this.s.connState;
      if (st === 'ready') return '#79BFA1';
      if (st === 'thinking') return '#F76734';
      if (st === 'rate_limited' || st === 'launching' || st === 'connecting') return '#F1BD6C';
      return '#BBBCBC';
    },
    drawerRows() {
      return this.pastSessions.map((ps) => {
        const isOpen = ps.session_id === this.entry.sessionId && this.sessionActive;
        return {
          ...ps,
          isOpen,
          title: isOpen && this.s.title ? this.s.title : ps.title,
          description: isOpen ? this.s.description : ps.description,
        };
      });
    },
    conversationTitle() {
      if (this.s.title) return this.s.title.length > 90 ? `${this.s.title.slice(0, 90)}…` : this.s.title;
      if (this.metricId) return `Metric ${this.metricId}`;
      if (this.studyId) return `Study ${this.studyId}`;
      if (this.s3Key) return this.s3Key.split('/').pop();
      return 'Interactive Agent';
    },
    agentLabel() {
      return 'Agent';
    },
    userInitials() {
      const u = (this.$store && this.$store.state && this.$store.state.user) || {};
      const name = u.full_name || u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '';
      return this.initialsOf(name);
    },
    persistedArtifacts() {
      return this.s.artifacts.filter((a) => !['visual', 'drawing', 'file', 'plan'].includes(a.kind));
    },
    notebookState() {
      const absorbed = new Set(this.s.drafts.map((d) => `preview_${d.id}`.slice(0, 64)));
      const blocks = this.s.notebook.filter((c) => !absorbed.has(c.id)).length;
      const drafts = this.s.drafts.length;
      const parts = [];
      if (blocks) parts.push(`${blocks} ${blocks === 1 ? 'block' : 'blocks'}`);
      if (drafts) parts.push(`${drafts} ${drafts === 1 ? 'draft' : 'drafts'}`);
      return parts.join(' · ') || 'Nothing yet';
    },
    notebookItems() {
      const flow = this.s.artifacts.filter((a) => NOTEBOOK_ARTIFACT_KINDS.has(a.kind));
      const absorbed = new Set(this.s.drafts.map((d) => `preview_${d.id}`.slice(0, 64)));
      const cells = this.s.notebook.filter((c) => !absorbed.has(c.id));
      const drafts = this.s.drafts.map((d) => ({ ...d, kind: 'draft', draftKind: d.kind, _ref: d }));
      return [...cells, ...drafts, ...flow].sort((x, y) => (x.order || 0) - (y.order || 0));
    },
    outputItems() {
      const absorbed = new Set(this.s.drafts.map((d) => `preview_${d.id}`.slice(0, 64)));
      const cells = this.s.notebook.filter((c) => !absorbed.has(c.id)).map((c) => ({
        key: c.key, order: c.order, iconKind: c.engine, label: this.engineLabel(c.engine),
        name: c.label || c.id, mono: !c.label, dot: this.cellStatus(c).tone === 'is-error' ? '#D53D3D' : (c.result ? '#389A6C' : '#BBBCBC'),
      }));
      const flow = this.s.artifacts.filter((a) => NOTEBOOK_ARTIFACT_KINDS.has(a.kind)).map((a) => ({
        key: a.key, order: a.order, iconKind: a.kind, label: a.label, name: a.name, mono: !!a.mono, dot: this.statusDot(a.status),
      }));
      return [...cells, ...flow].sort((x, y) => (x.order || 0) - (y.order || 0));
    },
    filesItems() {
      const represented = new Set(this.s.drafts.filter((d) => d.savedId).map((d) => `${DRAFT_LEDGER_KIND[d.kind] || d.kind}:${d.savedId}`));
      return this.s.artifacts.filter((a) => !NOTEBOOK_ARTIFACT_KINDS.has(a.kind) && !represented.has(`${a.kind}:${a.id}`));
    },
    sessionPill() {
      const drafts = this.s.drafts.filter((d) => d.status !== 'saved').length;
      if (drafts && !this.persistedArtifacts.length) return { text: `${drafts} ${drafts === 1 ? 'draft' : 'drafts'} · nothing saved`, bg: '#F4F4F4', fg: '#626264', dot: '#F1BD6C' };
      const inProd = this.persistedArtifacts.some((a) => (a.runs && a.runs.length) || a.status === 'running in production');
      if (inProd) return { text: 'In production', bg: '#F1F9F0', fg: '#485446', dot: '#389A6C' };
      if (this.persistedArtifacts.length) {
        return { text: 'Saved', bg: '#F4F4F4', fg: '#626264', dot: '#389A6C' };
      }
      return { text: 'Nothing saved', bg: '#F4F4F4', fg: '#626264', dot: '#BBBCBC' };
    },
    emptyTitle() {
      return 'Start a session.';
    },
    emptyBody() {
      return 'Every tool, no script. It waits for you.';
    },
    artifactSummary() {
      const n = this.s.artifacts.filter((a) => !['visual', 'drawing', 'file', 'plan'].includes(a.kind)).length;
      if (!n) return 'Nothing was saved.';
      return `${n} saved ${n === 1 ? 'artifact remains' : 'artifacts remain'}.`;
    },
    composerPlaceholder() {
      if (this.s.connState === 'closed') return this.stateLabel;
      if (this.s.connState === 'idle') return 'Start a session to begin';
      if (this.hasPendingQuestion) return 'Answer the decision above to continue';
      return 'Describe what you need… (Enter to send)';
    },
  },
  async mounted() {
    this.entry.view = this;
    this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
    this.scrollChat();
    this.s.artifacts
      .filter((a) => a.kind === 'run' && ['pending', 'running'].includes(a.status))
      .forEach((a) => this.startRunPolling(a.id));
    this.s.notebook
      .filter((c) => c.result && c.result.kind === 'r' && ['pending', 'running'].includes(c.result.status))
      .forEach((c) => this.startRunPolling(c.result.runId));
    if (!this.sessionActive) {
      const reattached = await this.reattach();
      if (!reattached && this.autoStart) this.connect();
    }
  },
  beforeUnmount() {
    if (this.entry.view === this) this.entry.view = null;
    clearInterval(this.ticker);
    Object.keys(this.runPollTimers).forEach((id) => this.stopRunPolling(id));
  },
  methods: {
    async approveOperation(block, approved) {
      if (block.answered || block.posting) return;
      block.posting = true;
      block.error = '';
      try {
        await axios.post(turnUrl(this.entry), {
          type: 'approval', operation_id: block.operationId, approved,
        });
        block.answered = approved ? 'Approved' : 'Declined';
        wakePolling(this.entry);
      } catch (error) {
        block.error = error.response?.data?.error || 'Could not record your decision. Retry to check the same proposal.';
      } finally {
        block.posting = false;
      }
    },
    resetConversation() {
      const s = this.s;
      s.closeReason = '';
      s.blocks = [];
      s.artifacts = [];
      s.notebook = [];
      s.runCells = {};
      s.files = [];
      s.drafts = [];
      s.totalCost = 0;
      s.title = '';
      s.description = '';
      s.promptTokens = null;
      s.contextLimit = null;
      s.continuedBy = '';
      this.entry.order = 0;
      this.entry.cursor = 0;
      this.entry.ownSeqs = new Set();
      Object.keys(this.runPollTimers).forEach((id) => this.stopRunPolling(id));
    },
    startRunPolling(runId) {
      if (!runId || this.runPollTimers[runId]) return;
      const poll = async () => {
        try {
          const { data } = await axios.get(`/api/impact_analytics/study-runs/${runId}/`);
          applyRunOutput(this.entry, {
            run_id: data.id,
            status: data.status,
            error: data.error_message || '',
            console: data.console_output || '',
            results: data.results || {},
          });
        } catch {
          this.stopRunPolling(runId);
        }
      };
      this.runPollTimers[runId] = setInterval(poll, 2500);
      poll();
    },
    stopRunPolling(runId) {
      if (this.runPollTimers[runId]) clearInterval(this.runPollTimers[runId]);
      delete this.runPollTimers[runId];
    },
    async fetchRunPlots(runId) {
      const holder = runHolder(this.entry, runId);
      if (!holder || holder.status !== 'completed') return;
      const urls = {};
      for (const output of (holder.outputs || []).filter((o) => o.kind === 'plot')) {
        const file = plotKey(output);
        const name = file.replace(/^plots\//, '').replace(/\.png$/, '');
        if (!name || holder.plotUrls[file]) {
          urls[file] = holder.plotUrls[file] || '';
          continue;
        }
        try {
          const response = await axios.get(
            `/api/impact_analytics/study-runs/${runId}/artifact/`,
            { params: { name }, responseType: 'blob' },
          );
          urls[file] = URL.createObjectURL(response.data);
        } catch {
          urls[file] = '';
        }
      }
      if (holder.kind === 'r') holder.plotUrls = urls;
      else upsertArtifact(this.entry, { key: holder.key, plotUrls: urls });
    },
    kindIcon(kind) {
      return KIND_ICONS[kind] || faFileLines;
    },
    kindColor(kind) {
      return KIND_COLORS[kind] || '#626264';
    },
    engineLabel(engine) {
      return ENGINE_LABELS[engine] || engine;
    },
    editorRows(text) {
      return Math.min(Math.max(String(text || '').split('\n').length + 1, 3), 30);
    },
    formatCount(n) {
      return typeof n === 'number' ? n.toLocaleString() : String(n ?? '');
    },
    cellStale(cell) {
      return !!(cell.result && cell.result.version !== cell.version);
    },
    cellStatus(cell) {
      const r = cell.result;
      if (!r) return { text: 'not run', tone: 'is-muted' };
      if (r.kind === 'r') {
        if (['pending', 'running'].includes(r.status)) return { text: r.status === 'pending' ? 'queued' : 'running…', tone: 'is-busy' };
        if (r.status === 'failed') return { text: 'failed', tone: 'is-error' };
        return { text: `run ${r.runId} · ${(r.outputs || []).length} outputs`, tone: 'is-ok' };
      }
      if (!r.ok) return { text: 'error', tone: 'is-error' };
      const secs = typeof r.elapsedMs === 'number' ? ` · ${(r.elapsedMs / 1000).toFixed(1)}s` : '';
      return { text: `${this.formatCount(r.rowCount)} rows${secs}`, tone: 'is-ok' };
    },
    startEdit(cell) {
      cell.draft = cell.code;
      cell.editing = true;
      cell.error = '';
    },
    cancelEdit(cell) {
      cell.editing = false;
      cell.draft = '';
    },
    async saveEdit(cell) {
      const code = String(cell.draft || '');
      if (!code.trim() || cell.saving) return;
      if (code === cell.code) {
        this.cancelEdit(cell);
        return;
      }
      cell.saving = true;
      cell.error = '';
      try {
        const { data } = await axios.post(turnUrl(this.entry), {
          type: 'block_edit', block_id: cell.id, engine: cell.engine, code,
        });
        if (data.seq) this.entry.ownSeqs.add(data.seq);
        cell.code = code;
        cell.version += 1;
        cell.by = 'user';
        cell.editing = false;
        cell.draft = '';
      } catch (err) {
        cell.error = (err && err.response && err.response.data && err.response.data.error) || 'Could not save the edit.';
      } finally {
        cell.saving = false;
      }
    },
    runCell(cell) {
      if (cell.editing) return;
      this.sendUserText(ACTION_TEXT.run(cell.id), { verb: 'run', id: cell.id });
    },
    actionVerb(verb) {
      return ACTION_VERBS[verb] || 'Running';
    },
    draftAction(verb, item) {
      const draft = item._ref || item;
      if (draft.editing) return;
      this.sendUserText(ACTION_TEXT[verb](draft.id), { verb, id: draft.id });
    },
    draftDot(d) {
      if (d.error) return '#D53D3D';
      if (d.status === 'saved') return '#389A6C';
      if (d.status === 'changed') return '#F1BD6C';
      return '#BBBCBC';
    },
    draftChip(d) {
      if (d.status === 'saved') return `Saved #${d.savedId}`;
      if (d.status === 'changed') return `Changed since save #${d.savedId}`;
      return 'Draft';
    },
    codeField(d) {
      const kind = d.draftKind || d.kind;
      if (kind === 'data_attribute') return 'sql_query';
      if (kind === 'study') return 'script';
      return '';
    },
    draftJson(d) {
      const rest = { ...(d.spec || {}) };
      const field = this.codeField(d);
      if (field) delete rest[field];
      return JSON.stringify(rest, null, 2);
    },
    draftCode(d) {
      const kind = d.draftKind || d.kind;
      if (kind === 'data_attribute') return d.spec.sql_query || '';
      if (kind === 'study') return d.spec.script || '';
      return '';
    },
    toggleDraft(item) {
      const d = item._ref || item;
      d.collapsed = !d.collapsed;
    },
    startDraftEdit(item) {
      const d = item._ref || item;
      d.collapsed = false;
      d.edit = { json: this.draftJson(d), code: this.codeField(d) ? (d.spec[this.codeField(d)] || '') : '' };
      d.editing = true;
      d.editError = '';
    },
    cancelDraftEdit(item) {
      const d = item._ref || item;
      d.editing = false;
      d.edit = {};
    },
    async saveDraftEdit(item) {
      const d = item._ref || item;
      if (d.saving) return;
      let spec;
      try {
        spec = JSON.parse(d.edit.json || '');
      } catch (err) {
        d.editError = `Not valid JSON: ${err.message}`;
        return;
      }
      if (!spec || typeof spec !== 'object' || Array.isArray(spec)) {
        d.editError = 'The spec must be a JSON object.';
        return;
      }
      const field = this.codeField(d);
      if (field) {
        if (!String(d.edit.code || '').trim()) { d.editError = `${field === 'script' ? 'The script' : 'The SQL'} cannot be empty.`; return; }
        spec[field] = d.edit.code;
      }
      if (JSON.stringify(spec) === JSON.stringify(d.spec)) { this.cancelDraftEdit(d); return; }
      d.saving = true;
      d.editError = '';
      try {
        const { data } = await axios.post(turnUrl(this.entry), { type: 'draft_edit', draft_id: d.id, spec });
        if (data.seq) this.entry.ownSeqs.add(data.seq);
        applyDraftEdit(this.entry, d.id, spec);
        d.editing = false;
        d.edit = {};
      } catch (err) {
        d.editError = (err && err.response && err.response.data && err.response.data.error) || 'Could not save the edit.';
      } finally {
        d.saving = false;
      }
    },
    factsFor(a) {
      const facts = [];
      Object.entries(a.detail || {}).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        facts.push({ k: String(k).replace(/_/g, ' '), v: String(v) });
      });
      return facts;
    },
    statusDot(status) {
      const st = String(status || '').toLowerCase();
      if (['running in production', 'completed', 'live', 'success', 'succeeded', 'validated', 'verified'].includes(st)) return '#389A6C';
      if (['failed', 'rejected', 'deleted', 'error'].includes(st)) return '#D53D3D';
      if (['running', 'queued', 'pending', 'in_progress', 'building'].includes(st)) return '#F1BD6C';
      return '#BBBCBC';
    },
    initialsOf(name) {
      return String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'Y';
    },
    async connect() {
      const s = this.s;
      s.connState = 'connecting';
      this.resetConversation();
      try {
        const { data } = await axios.post('/api/impact_analytics/agent-sessions/start/', {
          protocol_version: 2,
          metric_id: this.metricId || null,
          study_id: this.studyId || null,
          s3_key: this.s3Key || '',
          schema_id: this.schemaId || null,
          resume_session_id: this.resumeId || '',
          continue_from: this.continueFrom || '',
        });
        this.entry.sessionId = data.session_id;
        this.entry.cursor = data.cursor || 0;
        try { sessionStorage.setItem(this.storageKey, data.session_id); } catch { /* fine */ }
        s.connState = 'launching';
        wakePolling(this.entry);
      } catch (err) {
        s.connState = 'closed';
        s.closeReason = (err && err.response && err.response.data
          && err.response.data.error) || 'Could not start the session.';
      }
    },
    async reattach() {
      let stored = null;
      try { stored = sessionStorage.getItem(this.storageKey); } catch { /* fine */ }
      if (!stored) return false;
      try {
        const { data } = await axios.get(
          `/api/impact_analytics/agent-sessions/${stored}/stream/`,
          { params: { after: 0 } },
        );
        if (data.status !== 'active') {
          sessionStorage.removeItem(this.storageKey);
          return false;
        }
        this.resetConversation();
        this.entry.sessionId = stored;
        for (const ev of data.events || []) {
          if (ev.seq > this.entry.cursor) this.entry.cursor = ev.seq;
          handleEvent(this.entry, ev);
        }
        applyEnvelope(this.entry, data);
        fetchArtifacts(this.entry);
        wakePolling(this.entry);
        return true;
      } catch {
        return false;
      }
    },
    endSession() {
      const entry = this.entry;
      stopPolling(entry);
      try { sessionStorage.removeItem(this.storageKey); } catch { /* fine */ }
      if (entry.sessionId) {
        axios.post(turnUrl(entry), { type: 'end' }).catch(() => {});
      }
      pushBlock(entry, { kind: 'session_end' });
      this.s.connState = 'closed';
      this.s.thinkingSince = null;
      this.s.closeReason = 'Session ended.';
    },
    startLive() {
      this.showHistory = false;
      this.resumeId = null;
      this.continueFrom = '';
      this.connect();
    },
    continueSession() {
      const previous = this.entry.sessionId;
      if (!previous) return;
      if (this.sessionActive) {
        axios.post(turnUrl(this.entry), { type: 'end' }).catch(() => {});
      }
      this.showHistory = false;
      this.resumeId = null;
      this.continueFrom = previous;
      this.connect();
    },
    async resumeSession(sessionId) {
      if (sessionId === this.entry.sessionId && this.sessionActive) {
        this.showHistory = false;
        return;
      }
      const { data } = await axios.get(`/api/impact_analytics/agent-sessions/${sessionId}/`);
      this.showHistory = false;
      this.resumeId = sessionId;
      this.continueFrom = '';
      await this.connect();
      (data.events || []).forEach((ev) => handleEvent(this.entry, ev));
      this.s.blocks.forEach((b) => {
        if (b.kind === 'question' && !b.answered) b.answered = '(expired)';
      });
      this.s.blocks = this.s.blocks.filter((b) => b.kind !== 'session_end');
      pushBlock(this.entry, { kind: 'meta', text: 'Continued from an earlier session' });
      fetchArtifacts(this.entry);
    },
    async toggleHistory() {
      this.showHistory = !this.showHistory;
      if (this.showHistory) {
        const { data } = await axios.get('/api/impact_analytics/agent-sessions/');
        this.pastSessions = Array.isArray(data) ? data : (data.results || []);
      }
    },
    scrollChat() {
      this.$nextTick(() => {
        const el = this.$refs.scroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    scrollArtifacts() {
      this.$nextTick(() => {
        const el = this.$refs.artifactScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    artifactDomId(key) {
      return `artifact-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
    },
    scrollToArtifact(key) {
      const el = document.getElementById(this.artifactDomId(key));
      const sc = this.$refs.artifactScroll;
      if (el && sc) {
        const top = el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop - 16;
        sc.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      } else if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      const target = this.s.drafts.find((d) => d.key === key)
        || this.s.notebook.find((c) => c.key === key)
        || this.s.artifacts.find((a) => a.key === key);
      if (target) {
        if (target.kind === 'draft' || this.s.drafts.includes(target)) target.collapsed = false;
        target.flash = true;
        setTimeout(() => { target.flash = false; }, 1200);
      }
    },
    draftPreview(a) {
      const id = `preview_${a.id}`.slice(0, 64);
      return this.s.notebook.find((c) => c.id === id) || null;
    },
    previewStale(a) {
      const d = a._ref || a;
      return typeof d.previewedVersion === 'number' && d.version > d.previewedVersion;
    },
    previewDiffers(a) {
      const p = this.draftPreview(a);
      if (!p) return false;
      const inner = String(p.code || '').replace(/^SELECT \* FROM \(\s*/i, '').replace(/\s*\) _preview LIMIT \d+\s*$/i, '').trim();
      return inner !== String(this.draftCode(a) || '').trim();
    },
    drawingDoc,
    renderMarkdown,
    renderInline,
    drawingAspect(a) {
      const m = /viewBox\s*=\s*["']\s*[\d.-]+\s+[\d.-]+\s+([\d.]+)\s+([\d.]+)/.exec(a.svg || '');
      if (m && Number(m[2]) > 0) return `${m[1]} / ${m[2]}`;
      return '2 / 1';
    },
    formatWhen(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    sendUserText(text, action = null) {
      const entry = this.entry;
      if (!text || !entry.sessionId || !this.sessionActive) return false;
      if (action) {
        pushBlock(entry, { kind: 'action', verb: action.verb, targetId: action.id });
      } else {
        if (!this.s.title) this.s.title = text;
        pushBlock(entry, { kind: 'user', text });
      }
      if (this.s.connState !== 'launching') {
        this.s.connState = 'thinking';
        this.s.thinkingSince = Date.now();
      }
      entry.posting += 1;
      axios.post(turnUrl(entry), action ? { type: 'user', text, action } : { type: 'user', text })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch((err) => {
          pushBlock(entry, {
            kind: 'error',
            text: (err && err.response && err.response.data
              && err.response.data.error) || 'Message failed to send.',
          });
        })
        .finally(() => { entry.posting -= 1; wakePolling(entry); });
      return true;
    },
    sendDraft() {
      if (this.sendUserText(this.draft.trim())) this.draft = '';
    },
    onUploaded({ name, key }) {
      const entry = this.entry;
      pushBlock(entry, { kind: 'upload', ok: true, name, key });
      attachFile(entry, name, key);
      if (!entry.sessionId || !this.sessionActive) return;
      entry.posting += 1;
      axios.post(turnUrl(entry), { type: 'file_attached', name, key })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch((err) => {
          pushBlock(entry, {
            kind: 'error',
            text: (err && err.response && err.response.data && err.response.data.error) || 'The agent was not told about this file.',
          });
        })
        .finally(() => { entry.posting -= 1; });
    },
    onUploadFailed({ name, error }) {
      pushBlock(this.entry, { kind: 'upload', ok: false, name, error });
    },
    answerQuestion(block, label = '') {
      const entry = this.entry;
      const text = (block.responseText || '').trim();
      if (block.answered || (!label && !text) || !entry.sessionId) return;
      block.answered = label || text;
      entry.posting += 1;
      axios.post(turnUrl(entry), {
        type: 'user_answer', call_id: block.callId, label, text,
      })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch(() => {})
        .finally(() => { entry.posting -= 1; wakePolling(entry); });
    },
    interrupt() {
      const entry = this.entry;
      if (!entry.sessionId) return;
      axios.post(turnUrl(entry), { type: 'interrupt' })
        .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
        .catch(() => {})
        .finally(() => wakePolling(entry));
    },
  },
};
</script>

<style scoped>
.ae { display: flex; flex-direction: column; height: 100%; min-height: 0; flex: 1; box-sizing: border-box; padding: 20px; background: #FAFAFA; color: #353537; font-family: 'IBM Plex Sans', Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.45; -webkit-font-smoothing: antialiased; }
.ae-mono { font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; }
.ae-muted { color: #626264; }
.ae-small { font-size: 12px; }
.ae-break { word-break: break-all; }
.ae-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0; }

.ae-bar { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 4px 0 12px; }
.ae-state { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #626264; white-space: nowrap; margin-right: 8px; }
.ae-cost { font-size: 12px; color: #8E8F90; font-variant-numeric: tabular-nums; }
.ae-cost.is-stale { color: #9F4D30; }
.ae-cost.is-error { color: #D53D3D; font-weight: 600; }
.ae-link.is-urgent { color: #D53D3D; font-weight: 600; }
.ae-actions { display: flex; align-items: center; gap: 16px; }
.ae-link { font-size: 13px; color: #385883; text-decoration: none; }
.ae-link:hover { color: #21426C; text-decoration: underline; }
.ae-link-muted { font-size: 12px; color: #8E8F90; background: none; border: 0; padding: 0; cursor: pointer; font-family: inherit; }
.ae-link-muted:hover { color: #626264; text-decoration: underline; }
.ae-btn { font-family: inherit; font-size: 13px; padding: 8px 12px; border-radius: 4px; border: 1px solid #4C6E97; background: #FFFFFF; color: #385883; cursor: pointer; white-space: nowrap; }
.ae-btn:hover { background: #EBF4FF; }
.ae-btn:disabled { opacity: 0.45; cursor: default; background: #FFFFFF; }
.ae-btn-primary { background: #F76734; border-color: #F76734; color: #FFFFFF; }
.ae-btn-primary:hover { background: #CC5F38; border-color: #CC5F38; }
.ae-btn-primary:disabled { background: #F76734; }
.ae-history-row { width: 100%; display: flex; flex-direction: column; gap: 2px; align-items: flex-start; text-align: left; padding: 10px 16px; border: 0; border-bottom: 1px solid #E8E9E9; background: #FFFFFF; cursor: pointer; font-family: inherit; font-size: 11px; }
.ae-history-row:last-child { border-bottom: 0; }
.ae-history-row:hover { background: #FAFAFA; }
.ae-history-row.is-open { background: #EBF4FF; }
.ae-history-title { width: 100%; font-size: 12.5px; color: #353537; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ae-history-desc { width: 100%; font-size: 11.5px; line-height: 1.4; color: #626264; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ae-history-meta { font-size: 11px; color: #8A8B8D; white-space: nowrap; }

.ae-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) 260px; gap: 20px; align-items: stretch; flex: 1; min-height: 0; }
.ae-grid.has-drawer { grid-template-columns: 260px minmax(0, 1fr) minmax(0, 2fr) 260px; }
.ae-drawer-list { flex: 1; min-height: 0; overflow-y: auto; }
.ae-card { background: #FFFFFF; border: 1px solid #E8E9E9; border-radius: 8px; display: flex; flex-direction: column; min-height: 0; min-width: 0; overflow: hidden; }
.ae-card-head { padding: 16px 20px; border-bottom: 1px solid #E8E9E9; display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.ae-card-head-row { height: 44px; padding: 0 20px; }
.ae-card-head-stack { flex-direction: column; align-items: stretch; justify-content: flex-start; gap: 4px; padding: 10px 20px; min-height: 64px; box-sizing: border-box; }
.ae-card-head-stack .ae-title { white-space: normal; overflow-wrap: anywhere; line-height: 1.3; }
.ae-card-head-stack .ae-head-right { justify-content: flex-start; flex-wrap: wrap; row-gap: 2px; }
.ae-card-head-row .ae-title { min-width: 0; flex: 1; }
.ae-card-head-text { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.ae-head-right { display: flex; align-items: center; gap: 14px; flex-shrink: 0; font-size: 12px; }
.ae-head-right .ae-link { font-size: 12px; }
.ae-head-right .ae-state { margin-right: 0; }
.ae-label { font-size: 11px; font-weight: 600; color: #626264; text-transform: uppercase; letter-spacing: 0.06em; }
.ae-title { font-size: 16px; font-weight: 600; color: #1E1F21; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ae-meta { font-size: 12px; color: #626264; }
.ae-pill { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; padding: 4px 12px; border-radius: 999px; white-space: nowrap; }

.ae-log { padding: 20px; display: flex; flex-direction: column; gap: 16px; flex: 1; min-height: 0; overflow-y: auto; background: #FAFAFA; }
.ae-empty { display: flex; flex-direction: column; gap: 8px; max-width: 560px; padding: 8px 0; }
.ae-empty-title { font-size: 13px; font-weight: 600; color: #1E1F21; }
.ae-empty-body { font-size: 12.5px; color: #626264; }
.ae-row { display: flex; gap: 12px; animation: aeFadeUp 320ms ease-out; }
.ae-col { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0; }
.ae-avatar { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-weight: 600; }
.ae-avatar-human { background: #F4F4F4; color: #353537; font-size: 9px; }
.ae-avatar-agent { background: #1E1F21; color: #FFFFFF; font-size: 10px; }
.ae-who { font-size: 12px; color: #626264; line-height: 20px; }
.ae-bubble { background: #FFFFFF; border: 1px solid #E8E9E9; border-radius: 8px; padding: 12px 16px; font-size: 13px; color: #353537; white-space: pre-wrap; overflow-wrap: anywhere; }
.ae-prose { white-space: normal; overflow-wrap: anywhere; }
.ae-report { background: #FFFFFF; border: 1px solid #E8E9E9; border-radius: 8px; padding: 12px 16px; display: flex; flex-direction: column; gap: 8px; min-width: 0; overflow-wrap: anywhere; }
.ae-report-title { font-size: 13px; font-weight: 600; color: #1E1F21; }
.ae-item { display: flex; gap: 8px; font-size: 12.5px; color: #353537; min-width: 0; }
.ae-item > span:last-child { min-width: 0; overflow-wrap: anywhere; }
.ae-check { color: #389A6C; flex-shrink: 0; font-weight: 600; }
.ae-clarify { background: #EBF4FF; border: 1px solid #C1D6F0; border-radius: 8px; padding: 12px 16px; display: flex; flex-direction: column; gap: 8px; min-width: 0; overflow-wrap: anywhere; }
.ae-clarify-q { font-size: 13px; font-weight: 600; color: #1E1F21; }
.ae-clarify-q :deep(p) { margin: 0; }
.ae-option { display: flex; gap: 8px; align-items: flex-start; text-align: left; font-size: 12.5px; color: #353537; background: none; border: 0; padding: 0; cursor: pointer; font-family: inherit; line-height: 1.45; }
.ae-option:hover:not(:disabled) strong { text-decoration: underline; }
.ae-option.is-chosen strong { color: #21426C; }
.ae-option.is-dim { color: #8E8F90; }
.ae-num { flex-shrink: 0; width: 18px; height: 18px; border-radius: 50%; background: #FFFFFF; border: 1px solid #A3C4EE; color: #385883; font-size: 11px; font-weight: 600; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.ae-option.is-chosen .ae-num { background: #385883; color: #FFFFFF; border-color: #385883; }
.ae-answer { display: flex; gap: 8px; margin-top: 4px; }
.ae-answer-input { flex: 1; border: 1px solid #C1D6F0; border-radius: 4px; background: #FFFFFF; padding: 8px 12px; font-size: 13px; font-family: inherit; color: #353537; }
.ae-answer-input:focus { outline: none; border-color: #4C6E97; }
.ae-answered { font-size: 12px; color: #385883; }
.ae-aside { margin-left: 32px; }
.ae-reasoning { margin-top: 6px; font-size: 12px; color: #626264; white-space: pre-wrap; line-height: 1.5; }
.ae-created { display: flex; gap: 8px; align-items: baseline; font-size: 12px; color: #353537; background: none; border: 0; padding: 0; cursor: pointer; font-family: inherit; text-align: left; min-width: 0; overflow-wrap: anywhere; }
.ae-created:hover span:last-child { text-decoration: underline; }
.ae-created.is-error { cursor: default; color: #D53D3D; }
.ae-typing { margin-left: 32px; font-size: 12px; color: #8E8F90; }
.ae-run-line { color: #626264; }
.ae-end { background: #F1F9F0; border: 1px solid #C6E6C1; border-radius: 8px; padding: 12px 16px; display: flex; flex-direction: column; gap: 4px; margin-left: 32px; }
.ae-end-title { font-size: 14px; font-weight: 600; color: #1E1F21; }
.ae-end-text { font-size: 12px; color: #485446; }
.ae-upload { margin-left: 32px; border-radius: 8px; padding: 10px 14px; display: flex; flex-direction: column; gap: 4px; border: 1px solid; }
.ae-upload.is-ok { background: #F1F9F0; border-color: #C6E6C1; }
.ae-upload.is-error { background: #FDECEC; border-color: #F28181; }
.ae-upload-title { font-size: 13px; font-weight: 600; color: #1E1F21; overflow-wrap: anywhere; }
.ae-upload.is-error .ae-upload-title { color: #D53D3D; }
.ae-upload-meta { font-size: 11px; color: #485446; overflow-wrap: anywhere; }
.ae-upload.is-error .ae-upload-meta { color: #D53D3D; }
.ae-composer { padding: 16px 20px; border-top: 1px solid #E8E9E9; background: #FFFFFF; }
.ae-composer-box { border: 1px solid #E8E9E9; border-radius: 8px; padding: 8px 8px 8px 16px; display: flex; align-items: center; gap: 12px; background: #FFFFFF; }
.ae-composer-box:focus-within { border-color: #4C6E97; }
.ae-composer-box.is-disabled { background: #FAFAFA; }
.ae-textarea { flex: 1; border: 0; background: transparent; resize: none; font-family: inherit; font-size: 13px; color: #353537; line-height: 1.5; padding: 4px 0; min-height: 28px; }
.ae-textarea:focus { outline: none; }
.ae-textarea::placeholder { color: #8E8F90; }

.ae-artifacts { padding: 20px; display: flex; flex-direction: column; gap: 20px; flex: 1; min-height: 0; min-width: 0; overflow-y: auto; }
.ae-artifact { display: flex; flex-direction: column; gap: 8px; min-width: 0; flex-shrink: 0; animation: aeFadeUp 320ms ease-out; }
.ae-artifact-name { text-transform: none; letter-spacing: 0; color: #1E1F21; }

.ae-cell { display: flex; flex-direction: column; gap: 0; min-width: 0; flex-shrink: 0; border: 1px solid #E3E4E7; border-radius: 3px; background: #FFFFFF; overflow: hidden; box-shadow: 0 1px 2px rgba(30, 31, 33, 0.06), 0 4px 12px rgba(30, 31, 33, 0.08); animation: aeFadeUp 320ms ease-out; }
.ae-cell-meta { display: flex; align-items: baseline; gap: 10px; font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; font-size: 11px; color: #8E8F90; padding: 7px 12px; min-width: 0; background: #F4F5F7; border-bottom: 1px solid #E8E9E9; }
.ae-cell-engine { color: #626264; flex-shrink: 0; }
.ae-cell-id { font-size: 12px; font-weight: 500; color: #1E1F21; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ae-cell-actions { margin-left: auto; display: flex; gap: 12px; flex-shrink: 0; font-family: 'IBM Plex Sans', Helvetica, Arial, sans-serif; }
.ae-cell-actions .ae-link { font-size: 12px; }
.ae-cell-box { border: 0; border-radius: 0; background: #FFFFFF; overflow: hidden; min-width: 0; }
.ae-cell-box-code { background: #FFFFFF; }
.ae-cell-box-result { border-top: 1px solid #E8E9E9; }
.ae-cell-strip { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 4px 12px; background: #FFFFFF; border-bottom: 1px solid #F0F1F2; font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; font-size: 10.5px; color: #626264; text-transform: uppercase; letter-spacing: 0.05em; }
.ae-cell-strip-result { background: #FFFFFF; text-transform: none; letter-spacing: 0; font-size: 11px; }
.ae-cell-box-result:has(.ae-cell-strip-result:last-child) .ae-cell-strip-result { border-bottom: 0; }
.ae-cell-strip .is-ok { color: #389A6C; }
.ae-cell-strip .is-error { color: #D53D3D; }
.ae-cell-strip .is-stale { color: #9F4D30; }
.ae-cell-strip .is-busy { color: #B8860B; }
.ae-cell-strip .is-muted { color: #8E8F90; }
.ae-cell-code { background: #FFFFFF; min-width: 0; }
.ae-cell.is-editing .ae-cell-box-code { box-shadow: inset 0 0 0 1px #4C6E97; }
.ae-cell-pre { margin: 0; padding: 10px 12px; font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; font-size: 12px; line-height: 1.5; white-space: pre; color: #E8E9E9; background: #1E1F21; overflow: auto; max-height: 420px; }
.ae-cell-pre-json { color: #1E1F21; background: #FFFFFF; }
.ae-cell-editor { display: block; width: 100%; box-sizing: border-box; margin: 0; padding: 10px 12px; border: 0; background: #1E1F21; resize: vertical; font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; font-size: 12px; line-height: 1.5; color: #E8E9E9; caret-color: #FFFFFF; white-space: pre; overflow: auto; }
.ae-cell-editor:focus { outline: none; }
.ae-cell-result { overflow: hidden; min-width: 0; }
.ae-cell-result-r { padding: 10px 12px; }
.ae-cell-scroll { max-height: 320px; overflow: auto; }
.ae-cell-table thead tr { background: #FFFFFF; }
.ae-cell-table th { text-transform: none; letter-spacing: 0; padding: 5px 10px; }
.ae-cell-table td { padding: 4px 10px; border-bottom: 1px solid #F4F4F4; white-space: nowrap; }
.ae-file-rownum { color: #8E8F90; width: 1%; white-space: nowrap; }
.ae-cell-note { font-size: 11px; color: #8E8F90; padding: 6px 12px; font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; }
.ae-cell-error { font-size: 12px; color: #D53D3D; padding: 8px 12px; white-space: pre-wrap; overflow-wrap: anywhere; }
.ae-cell > .ae-cell-error { border-top: 1px solid #E8E9E9; }

.ae-files { flex: 1; min-height: 0; overflow-y: auto; background: #FAFAFA; }
.ae-files-sect { font-size: 10.5px; font-weight: 600; color: #626264; text-transform: uppercase; letter-spacing: 0.06em; padding: 10px 12px 4px; background: #FAFAFA; }
.ae-draft-chip { margin-left: auto; flex-shrink: 0; font-size: 10.5px; padding: 1px 7px; border-radius: 999px; background: #F0F1F2; color: #626264; white-space: nowrap; }
.ae-draft-chip.is-saved { background: #F1F9F0; color: #2F7A55; }
.ae-draft-chip.is-changed { background: #FFF6E5; color: #9F4D30; }
.ae-cell-meta .ae-draft-chip { margin-left: 0; }
.ae-cell-strip-toggle { cursor: pointer; user-select: none; }
.ae-cell-box-json { border-top: 1px solid #E8E9E9; }
.ae-kind-icon { width: 12px; height: 12px; color: #626264; flex-shrink: 0; align-self: center; }
.ae-file-row .ae-kind-icon { width: 11px; height: 11px; }
.ae-draft-caret { display: inline-block; color: #8E8F90; width: 12px; font-size: 10px; }
.ae-cell-status { white-space: nowrap; font-size: 11px; }
.ae-cell-status.is-stale { color: #9F4D30; }
.ae-cell-status.is-error { color: #D53D3D; }
.ae-cell.is-flash { box-shadow: 0 0 0 2px #A3C4EE, 0 4px 12px rgba(30, 31, 33, 0.08); }
.ae-artifact.is-flash { box-shadow: 0 0 0 2px #A3C4EE; border-radius: 6px; }
.ae-draft-desc { font-size: 12.5px; color: #353537; line-height: 1.45; padding: 8px 12px 0; }
.ae-draft-saved { font-size: 11px; color: #2F7A55; padding: 6px 12px 0; font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; }
.ae-draft-facts { display: flex; flex-wrap: wrap; gap: 4px 18px; padding: 6px 12px 0; }
.ae-draft-fact { display: flex; gap: 6px; font-size: 11.5px; color: #353537; min-width: 0; }
.ae-draft-fact > span:first-child { color: #8E8F90; text-transform: capitalize; flex-shrink: 0; }
.ae-draft-check { font-family: 'IBM Plex Sans', Helvetica, Arial, sans-serif; font-size: 12px; color: #353537; padding: 8px 12px; }
.ae-draft-compiled { border-bottom: 1px solid #F0F1F2; }
.ae-draft-compiled summary { cursor: pointer; font-size: 11px; color: #626264; padding: 4px 12px; font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; }
.ae-draft-steps { display: flex; flex-direction: column; gap: 2px; padding: 6px 12px 0; }
.ae-draft-step { display: flex; gap: 8px; align-items: baseline; font-size: 11.5px; min-width: 0; }
.ae-draft-step > .ae-mono:first-child { color: #8E8F90; flex-shrink: 0; }
.ae-draft-step-src { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ae-draft-step-src.is-draft { color: #9F4D30; }
.ae-draft-field { display: flex; flex-direction: column; gap: 2px; padding: 8px 12px 0; }
.ae-draft-field:last-of-type { padding-bottom: 8px; }
.ae-draft-field-k { font-size: 10.5px; color: #626264; text-transform: uppercase; letter-spacing: 0.04em; }
.ae-draft-editor { border: 0; border-radius: 0; background: #FFFFFF; color: #1E1F21; caret-color: #1E1F21; font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; font-size: 12px; white-space: pre; padding: 10px 12px; }
.ae-files-empty { padding: 12px 16px; }
.ae-file { border-bottom: 1px solid #E8E9E9; }
.ae-file-row { width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 12px; border: 0; background: transparent; cursor: pointer; font-family: inherit; text-align: left; min-width: 0; }
.ae-file-row:hover { background: #F4F4F4; }
.ae-file-kind { font-size: 10.5px; color: #626264; text-transform: uppercase; letter-spacing: 0.04em; flex-shrink: 0; }
.ae-file-name { font-size: 12px; color: #1E1F21; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ae-file-body { display: flex; flex-direction: column; gap: 4px; padding: 0 12px 10px 28px; }
.ae-file-fact { display: flex; justify-content: space-between; gap: 12px; font-size: 11.5px; color: #353537; }
.ae-file-fact > span:first-child { color: #8E8F90; text-transform: capitalize; flex-shrink: 0; }
.ae-file-fact > span:last-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ae-file-runs-head { margin-top: 6px; }
.ae-file-script { border: 1px solid #E8E9E9; border-radius: 6px; max-height: 200px; font-size: 11px; margin: 4px 0; }
.ae-file-run { display: flex; gap: 6px; align-items: baseline; font-size: 11px; min-width: 0; }
.ae-file-run-when { color: #8E8F90; flex-shrink: 0; }
.ae-file-run-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ae-artifact-head { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; }
.ae-artifact-label { display: flex; align-items: center; gap: 8px; font-size: 11px; color: #626264; text-transform: uppercase; letter-spacing: 0.04em; }
.ae-artifact-meta { font-size: 11px; color: #8E8F90; white-space: nowrap; }
.ae-facts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; border: 1px solid #E8E9E9; border-radius: 8px; padding: 12px 16px; }
.ae-fact { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.ae-fact-k { font-size: 11px; color: #626264; text-transform: uppercase; letter-spacing: 0.04em; }
.ae-fact-v { font-size: 13px; color: #1E1F21; font-weight: 500; overflow-wrap: anywhere; }
.ae-box { border: 1px solid #E8E9E9; border-radius: 8px; padding: 12px 16px; min-width: 0; }
.ae-iframe { width: 100%; border: 0; display: block; }
.ae-plot { max-width: 100%; display: block; }
.ae-table-wrap { border: 1px solid #E8E9E9; border-radius: 8px; overflow: hidden; }
.ae-table-scroll { max-height: 320px; overflow: auto; }
.ae-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.ae-table thead tr { background: #FAFAFA; }
.ae-table th { text-align: left; font-weight: 600; color: #626264; padding: 8px 12px; border-bottom: 1px solid #E8E9E9; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; }
.ae-table td { padding: 8px 12px; border-bottom: 1px solid #E8E9E9; color: #353537; vertical-align: middle; }
.ae-table tbody tr:last-child td { border-bottom: 0; }
.ae-table-empty { padding: 16px 12px; color: #8E8F90; font-size: 12px; text-align: center; }
.ae-runcell { display: flex; gap: 8px; align-items: baseline; }
.ae-code { margin: 0; background: #1E1F21; color: #E8E9E9; border-radius: 8px; padding: 16px; font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; font-size: 12px; line-height: 1.55; overflow: auto; white-space: pre; max-width: 100%; min-width: 0; max-height: 480px; }
.ae-code-light { background: #FAFAFA; color: #353537; border: 1px solid #E8E9E9; margin-top: 8px; max-height: 200px; white-space: pre-wrap; }
.ae-output { display: flex; flex-direction: column; gap: 8px; }

.ae-prose :deep(p) { margin: 0 0 8px; }
.ae-prose :deep(p:last-child), .ae-prose :deep(ul:last-child), .ae-prose :deep(ol:last-child) { margin-bottom: 0; }
.ae-prose :deep(h1), .ae-prose :deep(h2), .ae-prose :deep(h3), .ae-prose :deep(h4) { font-size: 13px; font-weight: 600; color: #1E1F21; margin: 12px 0 4px; }
.ae-prose :deep(h1:first-child), .ae-prose :deep(h2:first-child), .ae-prose :deep(h3:first-child) { margin-top: 0; }
.ae-prose :deep(ul), .ae-prose :deep(ol) { margin: 4px 0 8px; padding-left: 20px; }
.ae-prose :deep(li) { margin: 2px 0; }
.ae-prose :deep(strong) { color: #1E1F21; font-weight: 600; }
.ae-prose :deep(code) { font-family: 'IBM Plex Mono', 'JetBrains Mono', monospace; font-size: 11.5px; background: #F4F4F4; border-radius: 3px; padding: 1px 5px; }
.ae-prose :deep(pre) { background: #1E1F21; color: #E8E9E9; border-radius: 8px; padding: 12px 16px; margin: 8px 0; overflow-x: auto; font-size: 12px; line-height: 1.55; }
.ae-prose :deep(pre code) { background: transparent; padding: 0; color: inherit; }
.ae-prose :deep(table) { border-collapse: collapse; font-size: 12px; margin: 8px 0; }
.ae-prose :deep(th), .ae-prose :deep(td) { padding: 6px 10px; border-bottom: 1px solid #E8E9E9; text-align: left; }
.ae-prose :deep(a) { color: #385883; }
.ae-prose :deep(hr) { border: 0; border-top: 1px solid #E8E9E9; margin: 10px 0; }

@keyframes aeFadeUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
</style>
